import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { LuxonDateAdapter, MAT_LUXON_DATE_ADAPTER_OPTIONS } from '@angular/material-luxon-adapter';
import { BasePetition, Category, NULL_PETITION } from '@ncessor/shared';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { DateTime } from 'luxon';
import { NcessorService } from '../services/ncessor.service';
import { ReplaySubject, Subject } from 'rxjs';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { NgFor, AsyncPipe } from '@angular/common';
import { MatDateRangeInput, MatStartDate, MatEndDate, MatDatepickerToggle, MatDateRangePicker } from '@angular/material/datepicker';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDivider } from '@angular/material/divider';

export const NCESSOR_DATE_FORMATS = {
  parse: {
    dateInput: 'yyyy-MM-dd',
  },
  display: {
    dateInput: 'yyyy-MM-dd',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'yyyy-MM-dd',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

@Component({
    selector: 'app-petition-editor',
    templateUrl: './petition-editor.component.html',
    styleUrls: ['./petition-editor.component.scss'],
    providers: [
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_LUXON_DATE_ADAPTER_OPTIONS],
        },
        { provide: MAT_DATE_FORMATS, useValue: NCESSOR_DATE_FORMATS },
    ],
    imports: [
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatSelect,
        NgFor,
        MatOption,
        MatDateRangeInput,
        MatStartDate,
        MatEndDate,
        MatDatepickerToggle,
        MatSuffix,
        MatDateRangePicker,
        MatCheckbox,
        MatDivider,
        AsyncPipe,
    ],
})
export class PetitionEditorComponent {
  // Public properties.

  @Input() isNewPetition = false;

  @Input() editPetition: BasePetition = NULL_PETITION;

  @Output()
  editPetitionChange: EventEmitter<BasePetition> = new EventEmitter<BasePetition>();

  // Internal properties.h
  categories$: Subject<Category[]> = new ReplaySubject();

  petitionForm!: FormGroup;

  // Public methods.

  constructor(private readonly fb: FormBuilder, private readonly ncessorService: NcessorService) {
    this.petitionForm = this.fb.group({
      text: [''],
      category: [''],
      dateRange: this.fb.group({
        begin: [''],
        end: [''],
      }),
      isAnnual: [false],
    });

    this.ncessorService.getCategories().subscribe(this.categories$);
    this.petitionForm.valueChanges.subscribe(() => {
      this.editPetitionChange.emit(this.retrieveValues());
    });
  }

  ngOnInit() {
    this.textControl.setValue(this.editPetition.text);

    this.categoryControl.setValue(this.editPetition.category || 'uncategorized');
    // Don't allow users to change category of a new petition (they should go to
    // the page for that category instead).
    if (this.isNewPetition && this.editPetition.category) {
      this.categoryControl.disable();
    } else {
      this.categoryControl.enable();
    }

    this.dateControl.setValue({
      begin: this.editPetition.beginDate || this.defaultBeginDate,
      end: this.editPetition.endDate || this.defaultEndDate,
    });

    this.isAnnualControl.setValue(this.editPetition.isAnnual);
  }

  // Internal methods.

  retrieveValues() {
    const outPetition: BasePetition = {
      text: this.textControl.value || '',
      category: this.categoryControl.value,
      beginDate: this.dateRangeBegin,
      endDate: this.dateRangeEnd,
      isAnnual: this.isAnnualControl.value || false,
    };
    return outPetition;
  }

  private get defaultBeginDate() {
    // For existing petitions, leave blank dates blank.
    if (!this.isNewPetition) {
      return null;
    }
    return DateTime.now().plus({ days: 1 }).toFormat('yyyy-MM-dd');
  }

  private get defaultEndDate() {
    // For existing petitions, leave blank dates blank.
    if (!this.isNewPetition) {
      return null;
    }
    return DateTime.now().plus({ days: 40 }).toFormat('yyyy-MM-dd');
  }

  private get textControl() {
    return this.petitionForm.controls['text'];
  }

  private get categoryControl() {
    return this.petitionForm.controls['category'];
  }

  private get dateControl() {
    return this.petitionForm.controls['dateRange'];
  }

  private get dateRangeBegin() {
    return this.dateValueToString(this.dateControl.value.begin || null);
  }

  private get dateRangeEnd() {
    return this.dateValueToString(this.dateControl.value.end || null);
  }

  private dateValueToString(inValue: string | null) {
    return inValue ? inValue.toString().substring(0, 10) : null;
  }

  private get isAnnualControl() {
    return this.petitionForm.controls['isAnnual'];
  }
}
